import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Button } from '../ui/button';
import { getCategoryName } from '../../utils/categoryNames';

const TABS = [
  { value: 'all', labelKey: 'messages.tabAll' },
  { value: 'buyer', labelKey: 'messages.tabBuyer' },
  { value: 'seller', labelKey: 'messages.tabSeller' },
];

/**
 * Shared filter UI for the messages experience: 3 tabs (Tous / Acheteur /
 * Vendeur) + a "Filtres" button that opens the category + sort modal.
 *
 * Stateless: parent owns the values and setters (typically via the
 * `useMessagesFilter` hook). Used by both `MessagesPage` and the
 * Profile → Messages tab so the two views are guaranteed identical.
 *
 * `testIdPrefix` lets each call site keep distinct testids
 * (e.g. `messages` vs `profile-messages`).
 */
export const MessagesFilterBar = ({
  activeTab,
  setActiveTab,
  categoryFilter,
  setCategoryFilter,
  sortOrder,
  setSortOrder,
  activeFilterCount,
  categories = [],
  testIdPrefix = 'messages',
}) => {
  const { t } = useTranslation();
  const [filterOpen, setFilterOpen] = useState(false);
  const [draftCategory, setDraftCategory] = useState(categoryFilter);
  const [draftSort, setDraftSort] = useState(sortOrder);

  const openFilters = () => {
    setDraftCategory(categoryFilter);
    setDraftSort(sortOrder);
    setFilterOpen(true);
  };
  const applyFilters = () => {
    setCategoryFilter(draftCategory);
    setSortOrder(draftSort);
    setFilterOpen(false);
  };
  const resetFilters = () => {
    setDraftCategory('all');
    setDraftSort('newest');
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        {/* Top tabs */}
        <div
          className="flex items-center gap-2 overflow-x-auto"
          role="tablist"
          data-testid={`${testIdPrefix}-tabs`}
        >
          {TABS.map((tab) => {
            const active = activeTab === tab.value;
            return (
              <button
                key={tab.value}
                role="tab"
                aria-selected={active}
                onClick={() => setActiveTab(tab.value)}
                className={`px-4 py-1.5 text-sm rounded-full border transition whitespace-nowrap ${
                  active
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-text_strong border-border hover:bg-surface'
                }`}
                data-testid={`${testIdPrefix}-tab-${tab.value}`}
              >
                {t(tab.labelKey)}
              </button>
            );
          })}
        </div>

        {/* Filtres button */}
        <button
          type="button"
          onClick={openFilters}
          className="relative inline-flex items-center gap-2 px-3 py-1.5 text-sm text-text_strong border border-border rounded-full bg-white hover:bg-surface transition flex-shrink-0 ml-2"
          data-testid={`${testIdPrefix}-filter-button`}
          aria-label={t('messages.filters')}
        >
          <Settings2 className="w-4 h-4" />
          <span className="hidden sm:inline">{t('messages.filters')}</span>
          {activeFilterCount > 0 && (
            <span
              className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-semibold text-white bg-primary rounded-full"
              data-testid={`${testIdPrefix}-filter-count`}
            >
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      <Dialog open={filterOpen} onOpenChange={setFilterOpen}>
        <DialogContent
          className="sm:max-w-md"
          data-testid={`${testIdPrefix}-filter-modal`}
        >
          <DialogHeader>
            <DialogTitle>{t('messages.filters')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div>
              <label className="text-sm font-medium text-text_strong block mb-2">
                {t('messages.filterCategory')}
              </label>
              <Select value={draftCategory} onValueChange={setDraftCategory}>
                <SelectTrigger data-testid={`${testIdPrefix}-filter-category-trigger`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('messages.allCategories')}</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {getCategoryName(cat.value, t)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-text_strong block mb-2">
                {t('messages.sort')}
              </label>
              <Select value={draftSort} onValueChange={setDraftSort}>
                <SelectTrigger data-testid={`${testIdPrefix}-filter-sort-trigger`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">{t('messages.sortNewest')}</SelectItem>
                  <SelectItem value="oldest">{t('messages.sortOldest')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="flex flex-row justify-between gap-2 sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={resetFilters}
              data-testid={`${testIdPrefix}-filter-reset`}
            >
              {t('messages.reset')}
            </Button>
            <Button
              type="button"
              onClick={applyFilters}
              data-testid={`${testIdPrefix}-filter-apply`}
            >
              {t('messages.apply')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
